import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import {
  Activity, AlertTriangle, CalendarClock, CheckCircle2, IndianRupee,
  Mail, RefreshCw, Send, ShieldCheck, Timer,
} from "lucide-react";

/**
 * Sprint 4B + 4C — Admin watchdog dashboard.
 *
 * super_admin only. Shows:
 *   • Scheduler status + next-fire times for the readiness and dunning jobs.
 *   • Recent run summaries.
 *   • Subscription roll-up by band (active / expiring_soon / in_grace / suspended).
 *   • Recent readiness + dunning email log rows.
 *   • Manual "Run now" buttons for each watchdog.
 */
export default function WatchdogPage() {
  const [status, setStatus] = useState(null);
  const [subs, setSubs] = useState(null);
  const [readinessLog, setReadinessLog] = useState([]);
  const [dunningLog, setDunningLog] = useState([]);
  const [busy, setBusy] = useState({ readiness: false, dunning: false });

  const refreshAll = () => {
    api.get("/admin/watchdog/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/readiness/email-log").then((r) => setReadinessLog(r.data.items || [])).catch(() => {});
    api.get("/admin/subscriptions/email-log").then((r) => setDunningLog(r.data.items || [])).catch(() => {});
  };

  useEffect(() => { refreshAll(); }, []);

  const runReadiness = async () => {
    setBusy((b) => ({ ...b, readiness: true }));
    try {
      await api.post("/admin/watchdog/readiness/run");
      refreshAll();
    } finally { setBusy((b) => ({ ...b, readiness: false })); }
  };

  const runDunning = async () => {
    setBusy((b) => ({ ...b, dunning: true }));
    try {
      await api.post("/admin/watchdog/dunning/run");
      refreshAll();
    } finally { setBusy((b) => ({ ...b, dunning: false })); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="watchdog-root">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Activity className="h-3 w-3" /> Watchdog Control
            </p>
            <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">
              Conference Readiness &amp; Subscription Dunning
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Two automated jobs keep Diplomexa running: a T-48h Conference
              Readiness Report and a 30/15/7/3/1-day annual-subscription dunning watchdog.
            </p>
          </div>
          <button
            type="button" onClick={refreshAll}
            className="px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-[#0A192F] flex items-center gap-1.5"
            data-testid="watchdog-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </header>

        <SchedulerStatusCard status={status} />

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <ReadinessCard
            status={status} log={readinessLog}
            onRun={runReadiness} busy={busy.readiness}
          />
          <DunningCard
            status={status} subs={subs} log={dunningLog}
            onRun={runDunning} busy={busy.dunning}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

function SchedulerStatusCard({ status }) {
  const ok = status?.scheduler_running;
  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="watchdog-status-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Scheduler
          </h2>
          <p className="mt-1 flex items-center gap-1.5 text-xs">
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`}
              data-testid={`scheduler-${ok ? "live" : "down"}`}
            />
            {ok ? "Running — APScheduler in-process" : "Not running"}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-right text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Next readiness</p>
            <p className="mt-0.5 font-semibold text-[#0A192F]" data-testid="next-readiness">
              {fmtTime(status?.next_readiness_run)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Next dunning</p>
            <p className="mt-0.5 font-semibold text-[#0A192F]" data-testid="next-dunning">
              {fmtTime(status?.next_dunning_run)}
            </p>
          </div>
        </div>
      </div>

      {status?.recent_runs?.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Recent runs</p>
          <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
            {status.recent_runs.slice(0, 5).map((r, i) => (
              <li key={i} className="flex items-center gap-2"
                  data-testid={`recent-run-${i}`}>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span className="text-[#0A192F] font-medium">{r.kind}</span>
                <span className="text-slate-400">{fmtTime(r.at)}</span>
                <span className="ml-auto text-slate-500">
                  {summariseRun(r)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ReadinessCard({ status, log, onRun, busy }) {
  return (
    <section
      className="rounded-2xl border-2 border-[#D4AF37] bg-white p-5"
      data-testid="readiness-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[#D4AF37]" />
            Conference Readiness (Sprint 4B)
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            48 hours before every event, every confirmed delegate gets a
            personalised readiness report with their score, LMS completion,
            allocation, position-paper status, profile completeness and a
            link to anything they still need to finish.
          </p>
        </div>
        <button
          type="button" onClick={onRun} disabled={busy}
          className="px-3 py-1.5 rounded-md bg-[#0A192F] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="run-readiness-now"
        >
          <Send className={`h-3 w-3 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Running…" : "Run now"}
        </button>
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        Next fire: <strong>{fmtTime(status?.next_readiness_run)}</strong>
      </p>

      <h3 className="mt-5 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
        Recent emails ({log.length})
      </h3>
      <ul className="mt-2 divide-y divide-slate-100 max-h-[480px] overflow-y-auto"
          data-testid="readiness-log">
        {log.length === 0 && (
          <li className="py-4 text-sm text-slate-400">
            No readiness emails sent yet. Click <em>Run now</em> to test.
          </li>
        )}
        {log.map((row, i) => (
          <li key={`${row.user_id}-${row.event_id}-${i}`}
              className="py-2.5 flex items-start justify-between gap-3"
              data-testid={`readiness-row-${i}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#0A192F] truncate">
                {row.email || "—"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {row.event_title || row.event_id}
              </p>
            </div>
            <div className="text-right text-[11px]">
              <ScorePill score={row.score} band={row.band} />
              <p className="mt-1 text-slate-400">{fmtTime(row.sent_at)}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DunningCard({ status, subs, log, onRun, busy }) {
  const counts = subs?.by_band || { active: 0, expiring_soon: 0,
                                    in_grace: 0, suspended: 0 };
  return (
    <section
      className="rounded-2xl border-2 border-[#0F4C81] bg-white p-5"
      data-testid="dunning-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-[#0F4C81]" />
            Subscription Dunning (Sprint 4C)
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Reminders fire at {(subs?.schedule_days || [30,15,7,3,1,0,-7,-14])
            .map((d) => d > 0 ? `T-${d}` : d === 0 ? "T0" : `T+${-d}`).join(" · ")}.
            Beyond {subs?.grace_period_days || 14} days into grace, the org is suspended.
          </p>
        </div>
        <button
          type="button" onClick={onRun} disabled={busy}
          className="px-3 py-1.5 rounded-md bg-[#0A192F] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="run-dunning-now"
        >
          <Send className={`h-3 w-3 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Running…" : "Run now"}
        </button>
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        Next fire: <strong>{fmtTime(status?.next_dunning_run)}</strong>
      </p>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <BandCounter label="Active" count={counts.active} colour="emerald" testid="band-active" />
        <BandCounter label="Expiring" count={counts.expiring_soon} colour="amber" testid="band-expiring" />
        <BandCounter label="In grace" count={counts.in_grace} colour="orange" testid="band-grace" />
        <BandCounter label="Suspended" count={counts.suspended} colour="rose" testid="band-suspended" />
      </div>

      <h3 className="mt-5 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
        Recent reminders ({log.length})
      </h3>
      <ul className="mt-2 divide-y divide-slate-100 max-h-[420px] overflow-y-auto"
          data-testid="dunning-log">
        {log.length === 0 && (
          <li className="py-4 text-sm text-slate-400">
            No dunning emails sent yet. Real annual subscribers will land
            here as their renewal dates approach.
          </li>
        )}
        {log.map((row, i) => (
          <li key={`${row.user_id}-${row.days}-${i}`}
              className="py-2.5 flex items-start justify-between gap-3"
              data-testid={`dunning-row-${i}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#0A192F] truncate flex items-center gap-2">
                <Mail className="h-3 w-3" /> {row.email || "—"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {row.plan_id} · {row.days > 0 ? `T-${row.days}d` : row.days === 0 ? "Day of" : `Grace +${-row.days}d`}
              </p>
            </div>
            <div className="text-right text-[11px] text-slate-400">
              {fmtTime(row.sent_at)}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BandCounter({ label, count, colour, testid }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    orange:  "bg-orange-50 text-orange-800 border-orange-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${map[colour]}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-0.5 text-lg font-bold">{count ?? 0}</p>
    </div>
  );
}

function ScorePill({ score, band }) {
  if (score == null) return null;
  const cls = band === "ready"     ? "bg-emerald-100 text-emerald-700"
            : band === "almost"    ? "bg-amber-100 text-amber-800"
            : band === "at_risk"   ? "bg-orange-100 text-orange-800"
            :                        "bg-rose-100 text-rose-700";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-bold ${cls}`}>
      {score}% · {band || "—"}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function summariseRun(run) {
  const r = run.result || {};
  if (run.kind.startsWith("readiness")) {
    return `${r.events_checked ?? 0} events · ${(r.summaries || []).reduce((s, x) => s + (x.sent || 0), 0)} emails`;
  }
  if (run.kind.startsWith("dunning")) {
    return `${r.checked ?? 0} subs · ${r.sent ?? 0} emails`;
  }
  return "";
}
